import { Component, inject, OnDestroy } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from 'environments/environment';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { LocalStorage } from './local-storage.class';
import { Routenames } from './route-names.enum';
import { RouteParamnames } from './route-param-names.enum';
import { Features } from './services/auth/features';
import { Functionalities } from './services/auth/functionalities';
import { Roles } from './services/auth/roles';
import { RouteParamsService } from './services/route-params.service';
import { Settings } from './settings.class';
import { Utils } from './utils.class';

@Component({
    template: '',
    standalone: false,
})
export class CodaltComponent implements OnDestroy {
    private readonly routeParamsService = inject(RouteParamsService);

    Utils = Utils;
    Settings = Settings;
    LocalStorage = LocalStorage;
    readonly isNativePlatform = Capacitor.isNativePlatform();
    environment = environment;
    readonly Functionalities = Functionalities;
    readonly Features = Features;
    readonly Roles = Roles;
    readonly Routenames = Routenames;
    readonly RouteParamnames = RouteParamnames;

    /**
     * Runs the callback with the selected active route parameters and runs it again only when
     * one of those parameters changes. The subscription is disposed automatically on destroy.
     *
     * @param paramNames Route parameter name or names to watch.
     * @param callback Receives an object containing only the selected parameters.
     */
    protected watchRouteParams<T extends RouteParamnames>(
        paramNames: T | T[],
        callback: (params: Record<T, string | number>) => void
    ): void {
        const names = (Array.isArray(paramNames) ? paramNames : [paramNames]) as T[];
        this.subscriptions.add(
            this.routeParamsService.changes
                .pipe(
                    map(params =>
                        names.reduce(
                            (selected, name) => {
                                selected[name] = params[name];
                                return selected;
                            },
                            {} as Record<T, string>
                        )
                    ),
                    distinctUntilChanged((previous, current) => names.every(name => previous[name] === current[name]))
                )
                .subscribe(callback)
        );
    }

    subscriptions = new Subscription();

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
